import React from 'react';

import {
  Box,
  Progress,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Tooltip,
} from '@chakra-ui/react';

import { FONTS_OPACITY } from '@/constant';
import {
  formatAvgEndDelta,
  formatCompletionRate,
  formatJobCount,
} from '@/utils/searchOutcomes';

interface KpiItem {
  label: string;
  value: string;
  tooltip?: string;
}

interface RelatedKpiRowProps {
  totalJobs?: number;
  completionRate?: number;
  avgEndDeltaMins?: number;
  uniqueEngineers?: number;
  extraItems?: KpiItem[];
}

export const RelatedKpiRow: React.FC<RelatedKpiRowProps> = ({
  totalJobs,
  completionRate,
  avgEndDeltaMins,
  uniqueEngineers,
  extraItems = [],
}) => {
  const items: KpiItem[] = [
    {
      label: 'Total jobs',
      value: formatJobCount(totalJobs),
      tooltip: 'Historical jobs in this related bucket',
    },
    {
      label: 'Completion',
      value: formatCompletionRate(completionRate),
      tooltip: 'Share of jobs completed successfully',
    },
    {
      label: 'Avg end delta',
      value: formatAvgEndDelta(avgEndDeltaMins),
      tooltip:
        'Average finish vs plan: late = positive, early = negative',
    },
    ...(typeof uniqueEngineers === 'number'
      ? [
          {
            label: 'Engineers',
            value: formatJobCount(uniqueEngineers),
            tooltip: 'Unique engineers who worked this site',
          },
        ]
      : []),
    ...extraItems,
  ];

  const rate =
    typeof completionRate === 'number' && !Number.isNaN(completionRate)
      ? Math.max(0, Math.min(100, completionRate))
      : null;

  return (
    <Box w='full' minW={0}>
      <SimpleGrid
        columns={{ base: 2, md: items.length }}
        spacing={3}
        mb={3}
        w='full'
        minW={0}
      >
        {items.map(item => (
          <Tooltip
            key={item.label}
            label={item.tooltip}
            isDisabled={!item.tooltip}
            hasArrow
            openDelay={300}
          >
            <Stat
              bg='glass-items.bg'
              border='1px solid'
              borderColor='border.light'
              rounded='xl'
              px={3}
              py={2.5}
              minW={0}
              overflow='hidden'
            >
              <StatLabel
                fontSize='xs'
                opacity={0.65}
                color='fonts.primary'
                noOfLines={1}
              >
                {item.label}
              </StatLabel>
              <StatNumber
                fontSize={{ base: 'md', md: 'lg' }}
                lineHeight='7'
                fontWeight='semibold'
                color='fonts.primary'
                noOfLines={1}
              >
                {item.value}
              </StatNumber>
            </Stat>
          </Tooltip>
        ))}
      </SimpleGrid>

      {rate != null ? (
        <Tooltip label={`${formatCompletionRate(rate)} completion rate`} hasArrow>
          <Box>
            <Progress
              value={rate}
              size='sm'
              rounded='full'
              bg='glass-items.bg'
              sx={{
                '& > div': {
                  backgroundColor:
                    rate >= 80
                      ? 'tags.green.font'
                      : rate >= 50
                        ? 'tags.gold.font'
                        : 'tags.red.font',
                },
              }}
              opacity={FONTS_OPACITY + 0.2}
            />
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );
};
